import { IoChevronDown, IoRefresh, IoCloseOutline } from "react-icons/io5";
import { useEffect, useMemo, useRef, useState } from "react";
import TooltipHorizon from "../../../components/tooltip";

const ColumnSelector = ({ selectedColumns, onColumnToggle, availableColumns, onResetColumns }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Group and filter columns based on search
    const groupedColumns = useMemo(() => {
        const filteredColumns = searchQuery
            ? availableColumns.filter(col =>
                col.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                col.category.toLowerCase().includes(searchQuery.toLowerCase())
            )
            : availableColumns;

        // Create a map of column order for consistent positioning
        const columnOrder = new Map();
        columnOrder.set('details.City, State', 0);
        columnOrder.set('details.State', 1);

        // Sort columns within each category
        const sortedColumns = filteredColumns.sort((a, b) => {
            const orderA = columnOrder.get(a.id) ?? 999;
            const orderB = columnOrder.get(b.id) ?? 999;
            return orderA - orderB;
        });

        return sortedColumns.reduce((acc, col) => {
            const category = col.category || 'Other';
            if (!acc[category]) acc[category] = [];
            acc[category].push(col);
            return acc;
        }, {});
    }, [availableColumns, searchQuery]);

    const isAllSelected = availableColumns.length === selectedColumns.length;

    const handleSelectAll = (checked) => {
        const allColumnIds = availableColumns.map(col => col.id);
        if (checked) {
            onColumnToggle(allColumnIds);
        } else {
            onColumnToggle([]);
        }
    };

    const handleSearchFocus = (e) => {
        e.stopPropagation();
        if (!isOpen) setIsOpen(true);
    };

    return (
        <div className="relative mb-4" ref={dropdownRef}>
            <div className="flex items-center w-full sm:w-96 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <input
                    type="text"
                    placeholder="Search columns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleSearchFocus}
                    className="flex-grow px-4 py-2 text-base text-gray-700 bg-white rounded-l-md focus:outline-none"
                />
                {searchQuery && (
                    <TooltipHorizon
                        content="Clear search"
                        placement="top"
                        extra="text-xs bg-opacity-50 text-gray-700"
                        trigger={
                            <button
                                onClick={() => setSearchQuery('')}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                <IoCloseOutline className="w-5 h-5" />
                            </button>
                        }
                    />
                )}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="px-3 py-2 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                    <IoChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                <TooltipHorizon
                    content="Reset to default columns"
                    placement="top"
                    extra="text-xs bg-opacity-50 text-gray-700"
                    trigger={
                        <button
                            onClick={onResetColumns}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                        >
                            <IoRefresh className="w-5 h-5" />
                        </button>
                    }
                />
            </div>


            {isOpen && (
                <div className="absolute z-[500] mt-2 w-96 bg-white border border-gray-200 rounded-md shadow-lg">
                    <div className="p-2 max-h-96 overflow-y-auto">
                        <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-200">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm font-semibold text-gray-700">Select All</span>
                        </label>

                        {Object.keys(groupedColumns).length === 0 && (
                            <div className="p-4 text-center text-gray-500">
                                No columns match your search
                            </div>
                        )}

                        {Object.entries(groupedColumns).map(([category, columns]) => (
                            <div key={category} className="mt-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2 px-2">{category}</h3>
                                <div className="space-y-1">
                                    {columns.map((column) => (
                                        <label
                                            key={column.id}
                                            className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedColumns.includes(column.id)}
                                                onChange={() => onColumnToggle([column.id])}
                                                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnSelector;